/* eslint-disable react/no-array-index-key, react/jsx-one-expression-per-line, no-nested-ternary */
import React from 'react'
import PropTypes from 'prop-types'
import { Trans, Plural } from '@lingui/macro'
import { v4 as uuid } from 'uuid'

import ImageTextSection from '../../../../components/common/ImageTextSection'

import idPassportCapture from '../../../../../assets/instructions/ID_passport_capture.png'

import {
  ATTRIBUTE_TRANS_MAP,
  DOCUMENT_SOURCE_TRANS_MAP,
  DOCUMENT_SUBTYPE_TEXT_MAP
} from './localisationMaps'
import { getIntersectingElements, shouldSpread } from '../../../../utils'

export const VALID_ATTRIBUTES = [
  'given_names',
  'family_name',
  'full_name',
  'date_of_birth',
  'postal_address',
  'structured_postal_address',
  'gender',
  'nationality',
  'document_images',
  'document_details'
]

export const ADDITIONAL_DOCUMENT_PARAGRAPH_ATTRIBUTES = [
  'gender',
  'nationality',
  'postal_address',
  'structured_postal_address'
]

const toggled = false
const canBeToggled = true


const mapAttributeToTransId = attribute => ATTRIBUTE_TRANS_MAP[attribute]
const mapDocumentSourceToTransId = name => DOCUMENT_SOURCE_TRANS_MAP[name]
const mapDocumentSubTypeToText = (subType) => {
  const subTypeText = DOCUMENT_SUBTYPE_TEXT_MAP[subType]
  return subTypeText ? ` (${subTypeText})` : ''
}

const getRequiredSourcesTransList = sources => (
  sources.reduce((sourceTransList, { name, subType }) => {
    const transId = mapDocumentSourceToTransId(name)
    const mappedSubType = mapDocumentSubTypeToText(subType)
    if (transId) sourceTransList.push([<Trans key={transId} id={transId} />, mappedSubType])
    return sourceTransList
  }, [])
)

const getAttributeTransList = attributes => (
  attributes.reduce((attributeTransList, attribute) => {
    const transId = mapAttributeToTransId(attribute)
    const idAlreadyAdded = !!attributeTransList.find(trans => trans.key === transId)
    if (transId && !idAlreadyAdded) attributeTransList.push(<Trans key={transId} id={transId} />)
    return attributeTransList
  }, [])
)

const getCommaSeparatedList = (transList) => {
  const AND_TEXT = <Trans key={uuid()}>and</Trans>

  return transList.map((translation, index) => {
    if (index === 0) return translation
    if (index === transList.length - 1) return [' ', AND_TEXT, ' '].concat(translation)
    return [', '].concat(translation)
  })
}

const getParagraphBullet = (content, key) => <span style={{ display: 'block', margin: '0.4em 0' }} key={`bullet-${key}`}>&nbsp;&nbsp;{' - '}{content}</span>

const getRequiredSourcesParagraph = (sourceTransList) => {
  const commaSeparatedDocumentsText = getCommaSeparatedList(sourceTransList)
  return <p><Trans>Documents required: </Trans> { commaSeparatedDocumentsText }.</p>
}

const getAdditionalDocumentsParagraph = (sourceTransList, attributeTransList) => {
  const formattedAttributeTransList = attributeTransList.map(trans => getParagraphBullet(trans, trans.key))

  // Note: keeping that as a constant with name so 'lingui' can reference the name
  const sourcesCount = sourceTransList.length
  return (
    <p>
      <Plural
        value={sourcesCount}
        one="If the document does not contain the following:"
        other="If the documents do not contain the following:"
      />
      {formattedAttributeTransList}
      <Trans>please add an additional document that includes this information.</Trans>
    </p>
  )
}

const getHeading = (requiredSources) => {
  // Note: keeping that as a constant with name so 'lingui' can reference the name
  const requiredSourcesCount = requiredSources.length
  return (
    <Plural
      value={requiredSourcesCount}
      one="Add an ID document"
      other="Add ID documents"
    />
  )
}

const getParagraphs = (requiredAttributes, requiredSources) => {
  const includedAttributes = getIntersectingElements(ADDITIONAL_DOCUMENT_PARAGRAPH_ATTRIBUTES, requiredAttributes)
  const attributeTransList = getAttributeTransList(includedAttributes)
  const sourceTransList = getRequiredSourcesTransList(requiredSources)
  const formattedTransList = sourceTransList.map(trans => <b key={`bold-${trans.key}`}>{trans}</b>)

  return [
    ...shouldSpread(sourceTransList.length, getRequiredSourcesParagraph(formattedTransList)),
    <Trans><p>Tap <b>ID documents</b> on the home screen.</p></Trans>,
    <Trans><p>Tap <b>Add ID document.</b></p></Trans>,
    ...shouldSpread(attributeTransList.length, getAdditionalDocumentsParagraph(sourceTransList, attributeTransList)),
    <Trans><p>This should only take a couple of minutes to complete and for an ID document to be approved.</p></Trans>
  ]
}

const isShown = (requiredAttributes) => {
  const includedAttributes = getIntersectingElements(VALID_ATTRIBUTES, requiredAttributes)
  return includedAttributes.length > 0
}

const IdDocumentStep = ({ requiredAttributes, requiredSources }) => {
  const heading = getHeading(requiredSources)
  const paragraphs = getParagraphs(requiredAttributes, requiredSources)

  return (
    <ImageTextSection
      contentToggled={toggled}
      contentToggleEnabled={canBeToggled}
      imgSrc={idPassportCapture}
    >
      <h6 className="instructions-section__header">{ heading }</h6>
      { paragraphs.map((p, key) => React.cloneElement(p, { key }))}
    </ImageTextSection>
  )
}

IdDocumentStep.isShown = isShown

IdDocumentStep.propTypes = {
  requiredAttributes: PropTypes.arrayOf(PropTypes.string).isRequired,
  requiredSources: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      subType: PropTypes.string
    }).isRequired
  ).isRequired,
}

export default IdDocumentStep
